import { useEffect, useId, useState } from 'react'
import {
  aktuellesZahlenformat,
  alsEingabe,
  dezimaltrenner,
  liesAnteil,
  liesDezimal,
  prozentzahl,
  speicherform,
  t,
  zahlenbeispiel,
} from './sprache'
import type { Zahlendarstellung } from './typen'

/** Was im Feld steht, als Speicherform gelesen — ein Anteil in Prozent, alles andere als Zahl. */
function gelesen(text: string, darstellung: Zahlendarstellung | null | undefined): string | null {
  return darstellung === 'anteil' ? liesAnteil(text) : liesDezimal(text)
}

/**
 * **Ein Feld für jede Kommazahl der Stammdaten** — Anteil, Betrag, Kurs (7.2
 * Abschnitt 22, R840 — Eine Zahl in den Stammdaten steht wie im Bericht;
 * Entscheidung des Auftraggebers vom 2026-09-30).
 *
 * **Befund aus der Durchsicht vom 2026-09-29:** Im Blatt einer Beteiligung
 * stand der Kapitalanteil als „0.80000000" in einem Zahlenfeld des Browsers.
 * „80" oder „80 %" dort und „1.600.000,00" bei den Anschaffungskosten endeten
 * in einem technischen Fehler statt in einem Satz.
 *
 * **Ein Bauteil, überall eingebettet** (R317) — dieselbe Bauweise wie das
 * Periodenfeld (R502, R839): Es zeigt den Wert in der Hausschreibweise, liest
 * die Eingabe nach dem gewählten Zahlenformat und gibt die **Speicherform**
 * weiter. Der Anwender denkt nie in ihr.
 *
 * **Ein Anteil wird in Prozent getippt** — „80", „80 %", „80,5" —, gespeichert
 * bleibt der Bruch. Das Prozentzeichen steht sichtbar hinter dem Feld: Wer aus
 * Gewohnheit „0,8" tippt, bekommt 0,8 %, und das Zeichen sagt es ihm sofort.
 *
 * **Getippt wird als Text.** Ein Zahlenfeld des Browsers nimmt je nach
 * Rechnersprache das Komma oder den Punkt nicht an und den Tausendertrenner
 * nie; hier gilt das Zahlenformat der Fußleiste (R162).
 *
 * **Eine Fehleingabe steht als Satz da, nicht als roter Rahmen** (R143, R145),
 * und erst, wenn der Anwender das Feld verlassen hat: Während des Tippens ist
 * jede Zwischenstufe kurz unvollständig. Solange die Eingabe keine Zahl
 * ergibt, sagt das Feld dem Aufrufer `null`.
 *
 * **Beim Verlassen steht die Hausschreibweise im Feld** — aus „80,50" wird
 * „80,5", aus „1600000" bei einem Betrag „1.600.000,00". Rundet die
 * Hausschreibweise dabei (ein Betrag trägt zwei Nachkommastellen), meldet das
 * Feld den gerundeten Wert: Im Feld und im Speicher steht dieselbe Zahl.
 *
 * **Rechtsbündig ist das Feld nicht** (R763): Ein Eingabefeld richtet sich
 * nach dem Formular, nicht nach der Tabelle.
 */
export function Zahlenfeld({
  wert,
  aufWert,
  darstellung,
  beschriftung,
  gesperrt = false,
  erstesFeld,
  klasse,
  erlaeuterung,
  aufVerlassen,
}: {
  /**
   * Der geltende Wert in der **Speicherform** — bei einem Anteil der Bruch
   * (`'0.8'`), sonst die Zahl mit Punkt (`'1600000.00'`). `null` heißt: keiner.
   */
  wert: string | null
  /** Der neue Wert in der Speicherform; `null`, solange die Eingabe keine Zahl ergibt. */
  aufWert: (wert: string | null) => void
  /** Anteil, Betrag oder Kurs — ohne Angabe eine Zahl ohne Endnullen. */
  darstellung?: Zahlendarstellung | null
  beschriftung: string
  gesperrt?: boolean
  erstesFeld?: React.Ref<HTMLInputElement>
  /**
   * Die Klasse der Umhüllung — Vorgabe `suchzeile`.
   *
   * **Leer, wo das Feld in einem Raster steht** — wie beim Periodenfeld.
   */
  klasse?: string
  /** Ein Satz unter dem Feld, wo einer dazugehört — wie an den Nachbarfeldern. */
  erlaeuterung?: string
  /**
   * Der Anwender hat das Feld verlassen: der Wert, wie er jetzt gilt, und ob
   * die Eingabe lesbar war. Ein leeres Feld ist lesbar und ergibt `null`.
   */
  aufVerlassen?: (wert: string | null, lesbar: boolean) => void
}) {
  const [text, setText] = useState(() => alsEingabe(wert, darstellung))
  const [beruehrt, setBeruehrt] = useState(false)
  const hilfeId = useId()

  const istAnteil = darstellung === 'anteil'

  // **Von außen gesetzt sticht** — aber nur, wenn dort eine andere Zahl steht
  // als die getippte. Verglichen wird über die Speicherform ohne Endnullen:
  // Der Server liefert „0.80000000", getippt ist „80", und beides ist
  // dieselbe Zahl. Sonst änderte sich das Feld unter den Fingern.
  useEffect(() => {
    setText((bisher) =>
      speicherform(gelesen(bisher, darstellung)) === speicherform(wert)
        ? bisher
        : alsEingabe(wert, darstellung),
    )
  }, [wert, darstellung])

  const falsch = beruehrt && text.trim() !== '' && gelesen(text, darstellung) === null

  // Die Schreibweisen stehen im Zahlenformat des Anwenders — ein Beispiel mit
  // Komma, wo er mit Punkt tippt, wäre eine Anleitung zur Fehleingabe.
  const beispiele: Record<string, string> = istAnteil
    ? { beispiel: prozentzahl('33.3333') }
    : { mit: zahlenbeispiel(aktuellesZahlenformat()), ohne: `1234567${dezimaltrenner()}5` }

  const umhuellung = [klasse ?? 'suchzeile', istAnteil ? 'anteilfeld' : '']
    .filter((k) => k !== '')
    .join(' ')

  return (
    <label className={umhuellung === '' ? undefined : umhuellung}>
      <span className="name">{beschriftung}</span>
      <input
        ref={erstesFeld}
        value={text}
        disabled={gesperrt}
        inputMode="decimal"
        title={t(istAnteil ? 'anteil.muster.hinweis' : 'zahl.muster.hinweis', beispiele)}
        // Wo das Raster die Beschriftung verbirgt (Klasse nurfeld in Tafel und
        // Blatt), trägt das Feld seinen Namen selbst — wie das Periodenfeld.
        aria-label={beschriftung}
        aria-describedby={falsch ? hilfeId : undefined}
        aria-invalid={falsch || undefined}
        onChange={(e) => {
          setText(e.target.value)
          aufWert(gelesen(e.target.value, darstellung))
        }}
        onBlur={() => {
          setBeruehrt(true)

          const getippt = gelesen(text, darstellung)
          if (getippt === null) {
            aufVerlassen?.(null, text.trim() === '')
            return
          }

          // Die Hausschreibweise — und was sie als Zahl bedeutet. Ein Betrag
          // wird dabei auf zwei Stellen gerundet; dann gilt der gerundete.
          const haus = alsEingabe(getippt, darstellung)
          const gilt = gelesen(haus, darstellung) ?? getippt

          setText(haus)
          if (gilt !== getippt) {
            aufWert(gilt)
          }
          aufVerlassen?.(gilt, true)
        }}
      />

      {/*
        **Das Zeichen steht hinter dem Feld und nicht darin:** Im Feld wäre es
        etwas, das der Anwender mitlöschen und mittippen müsste.
      */}
      {istAnteil && <span className="zahlenfeldzeichen">%</span>}

      {/*
        **Der Hilfetext steht im Feld und nicht daneben** (R431) — dieselbe
        Stelle und dieselbe Form wie beim Periodenfeld.
      */}
      {falsch && (
        <span className="hinweis periodenhilfe" id={hilfeId}>
          {t(istAnteil ? 'anteil.ungueltig' : 'zahl.ungueltig', beispiele)}
        </span>
      )}

      {erlaeuterung && !falsch && <span className="erlaeuterung">{erlaeuterung}</span>}
    </label>
  )
}
